import { MeetingRecordingListResponseSchema, type MeetingRecordingListQuery } from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiFetchUnchecked } from "./client.js";

const MEETINGS_KEY = ["meetings"] as const;

export function useMeetingRecordings(
  query?: Partial<MeetingRecordingListQuery>,
  options?: { tenantSlug?: string | null; enabled?: boolean },
) {
  const params = new URLSearchParams();
  if (query?.status) params.set("status", query.status);
  if (query?.search) params.set("search", query.search);
  if (query?.includeArchived) params.set("includeArchived", "true");
  if (query?.archivedOnly) params.set("archivedOnly", "true");
  if (query?.limit) params.set("limit", String(query.limit));
  if (query?.offset) params.set("offset", String(query.offset));
  const qs = params.toString();
  return useQuery({
    queryKey: [...MEETINGS_KEY, query, options?.tenantSlug ?? null],
    queryFn: () =>
      apiFetch(
        `/meetings${qs ? `?${qs}` : ""}`,
        { tenantOverride: options?.tenantSlug ?? undefined },
        MeetingRecordingListResponseSchema,
      ),
    enabled: options?.enabled ?? true,
  });
}

export function useMeetingDownload() {
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await apiFetchUnchecked<{ downloadUrl: string; filename: string }>(
        `/meetings/${id}/download`,
      );
      const a = document.createElement("a");
      a.href = result.downloadUrl;
      a.download = result.filename;
      a.click();
      return result;
    },
  });
}

export function useArchiveMeetingRecording() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetchUnchecked(`/meetings/${id}/archive`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}

export function useUnarchiveMeetingRecording() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetchUnchecked(`/meetings/${id}/unarchive`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}

export function useDeleteMeetingRecording() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<{ deleted: true; id: string }>(`/meetings/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}
